import { useState } from "react";
import { Lightbulb, MapPin, Route, UtensilsCrossed, BedDouble, Ticket, Send, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { tNL } from "../lib/journalI18n";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

export const SUGGESTION_KINDS = [
  { key: "plaats", label: "Plaats", Icon: MapPin },
  { key: "route", label: "Route", Icon: Route },
  { key: "restaurant", label: "Restaurant", Icon: UtensilsCrossed },
  { key: "verblijf", label: "Verblijf", Icon: BedDouble },
  { key: "activiteit", label: "Activiteit", Icon: Ticket },
  { key: "anders", label: "Anders", Icon: Lightbulb },
];

export const STATUS_STYLES = {
  nieuw: { label: "Nieuw", cls: "bg-[#3A6EA5]/10 text-[#3A6EA5]" },
  bekeken: { label: "Bekeken", cls: "bg-amber-500/10 text-amber-600" },
  toegevoegd: { label: "Toegevoegd aan de planning", cls: "bg-[#2D6A4F]/10 text-[#2D6A4F]" },
  afgewezen: { label: "Niet gepland", cls: "bg-red-500/10 text-red-500" },
};

export const kindMeta = (key) => SUGGESTION_KINDS.find((k) => k.key === key) || SUGGESTION_KINDS[5];

export const SuggestionItem = ({ s, days = [], children, t = tNL, locale = "nl-BE" }) => {
  const st = STATUS_STYLES[s.status] || STATUS_STYLES.nieuw;
  const { Icon } = kindMeta(s.kind);
  const day = s.day_number != null ? days.find((d) => d.day_number === s.day_number) : null;
  return (
    <div data-testid={`suggestion-item-${s.id}`} className="bg-card rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-1.5">
            <Icon className="w-3 h-3" /> {t(`kind_${kindMeta(s.kind).key}`)}{day ? ` · ${t("forDay")} ${s.day_number}` : ""}
          </p>
          <p className="font-heading font-bold text-sm mt-1">{s.title}</p>
        </div>
        <span data-testid={`suggestion-status-${s.id}`} className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full ${st.cls}`}>{t(`st_${s.status || "nieuw"}`)}</span>
      </div>
      {s.text && <p className="text-sm text-muted-foreground leading-relaxed mt-2">{s.text}</p>}
      {s.owner_note && (
        <div className="mt-2.5 bg-[#B5838D]/5 border-l-2 border-[#B5838D] rounded-r-xl px-3 py-2">
          <p className="text-xs leading-relaxed"><span className="font-semibold text-[#B5838D]">{t("ownersReply")}</span> {s.owner_note}</p>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-2 font-mono">
        {s.author_name} · {new Date(s.created_at).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
      </p>
      {children}
    </div>
  );
};

export const SuggestionBox = ({ token, suggestions = [], days = [], guestName, guestEmail, identityOk, saveIdentity, onRefresh, t = tNL, locale = "nl-BE" }) => {
  const [kind, setKind] = useState("plaats");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [dayNumber, setDayNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(suggestions.length === 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!identityOk) return toast.error(t("fillIdentityWho"));
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post(`/journal/${token}/suggestions`, {
        kind, title: title.trim(), text: text.trim(),
        author_name: guestName.trim(), author_email: guestEmail.trim(),
        day_number: dayNumber === "" ? null : Number(dayNumber),
      });
      saveIdentity();
      toast.success(t("sugThanks"));
      setTitle(""); setText(""); setDayNumber("");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="journal-suggestions-section">
      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2">
        <Lightbulb className="w-4 h-4" /> {t("sugTitle")}{suggestions.length > 0 ? ` (${suggestions.length})` : ""}
      </p>

      <div className="bg-card rounded-2xl p-5">
        <button type="button" data-testid="suggestion-form-toggle" onClick={() => setFormOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-left">
          <p className="font-heading font-bold text-sm flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-[#3A6EA5]" /> {t("sugPropose")}
          </p>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${formOpen ? "rotate-180" : ""}`} />
        </button>
        {formOpen && (
          <form onSubmit={submit} className="space-y-3 mt-3">
            <p className="text-xs text-muted-foreground">{t("sugIntro")}</p>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTION_KINDS.map(({ key, Icon }) => (
                <button key={key} type="button" data-testid={`suggestion-kind-${key}`} onClick={() => setKind(key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-[background-color,color] duration-200 ${kind === key ? "bg-[#3A6EA5] text-white" : "bg-secondary hover:bg-secondary/70"}`}>
                  <Icon className="w-3 h-3" /> {t(`kind_${key}`)}
                </button>
              ))}
            </div>
            <Input data-testid="suggestion-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={t("sugTitlePh")} className="rounded-xl h-10" maxLength={120} />
            <Textarea data-testid="suggestion-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
              placeholder={t("sugTextPh")} className="rounded-xl" maxLength={1000} />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <select data-testid="suggestion-day-select" value={dayNumber} onChange={(e) => setDayNumber(e.target.value)}
                className="rounded-xl h-9 px-3 text-xs bg-secondary border-none font-semibold">
                <option value="">{t("wholeTrip")}</option>
                {days.map((d) => (
                  <option key={d.day_number} value={d.day_number}>{`${t("day")} ${d.day_number} · ${d.date}`}</option>
                ))}
              </select>
              <Button data-testid="suggestion-submit" type="submit" disabled={busy || !title.trim()}
                className="rounded-full bg-[#3A6EA5] hover:bg-[#2d5884] text-white">
                <Send className="w-4 h-4 mr-1.5" /> {busy ? t("sending") : t("sugSend")}
              </Button>
            </div>
            {!identityOk && <p className="text-xs text-muted-foreground">{t("sugHint")}</p>}
          </form>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-2.5" data-testid="journal-suggestions-list">
          {[...suggestions].reverse().map((s) => <SuggestionItem key={s.id} s={s} days={days} t={t} locale={locale} />)}
        </div>
      )}
    </div>
  );
};
